import { audio } from '../systems/audio/AudioSystem.js';
import { LeaderboardClient } from '../systems/net/LeaderboardClient.js';
import { ASCENSION_ICON } from '../data/ascensions.js';
import { BOSSES } from '../data/bosses.js';
import { formatTime } from '../core/formatTime.js';
import { dailySeed, seedLabel } from '../data/seeds.js';

function gauntletIndexOf(entry) {
  const value = entry.gauntletIndex ?? entry.gauntlet_index;
  const index = Number(value);
  return Number.isFinite(index) ? Math.max(-1, index) : -1;
}

// A run that opted out of ALL permanent progression (no meta upgrades, no
// Shard perks, no pet bonuses) — the 1.5x purity multiplier in Currency.js.
// Snake_case fallbacks because rows come straight from postgres columns.
function isUnaided(entry) {
  return Boolean(entry.noEchoUpgrades ?? entry.no_echo_upgrades)
    && Boolean(entry.noShardPerks ?? entry.no_shard_perks)
    && Boolean(entry.noPetBonuses ?? entry.no_pet_bonuses);
}

function formatRunDetails(entry) {
  const ascension = entry.ascension ? `${ASCENSION_ICON} A${entry.ascension}` : 'Normal';
  const unaided = isUnaided(entry) ? ' ◇' : ''; // ◇ = unaided, see title below
  if (!entry.endless) return `${ascension}${unaided}`;
  const loop = Math.floor(Math.max(0, gauntletIndexOf(entry)) / BOSSES.length) + 1;
  return `∞ L${loop} ${ascension}${unaided}`;
}

/** Fetches and renders the global top-N leaderboard. First screen in the game with real network I/O — has explicit loading/error states. */
export class LeaderboardMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.category = 'all';
    // Act I is the board every player has rows on and the one a new run posts
    // to. `campaign` is still listed but is legacy: it holds the thirteen-boss
    // rows earned before the acts were split (data/acts.js), which a seven-boss
    // Act I clear can no longer reach, so no new run submits to it.
    this.board = 'act1';
    // Today's daily view (BULLET-HELL.md §D8): off by default, because the
    // all-runs board is still the board. When on, only rows that played today's
    // seed are shown — the one view where every listed run faced the same waves,
    // the same cards and the same offers, which is what the board could never
    // claim before seeds existed.
    this.dailyOnly = false;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay leaderboard-overlay';
    this._renderShell();
    this.root.appendChild(this.el);
    this._load();
  }

  _renderShell() {
    this.el.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'LEADERBOARD';
    this.el.appendChild(title);

    this.body = document.createElement('div');
    this.body.className = 'leaderboard-body';
    this.body.textContent = 'Loading…';
    this.el.appendChild(this.body);

    const filters = document.createElement('div');
    filters.className = 'settings-rows';
    // Two selects, and the board one comes first because it picks the ladder
    // rather than filtering within one — an Act III run and a campaign run are
    // not comparable scores (see data/acts.js), so they never share a list.
    filters.innerHTML = `
      <select data-board aria-label="Leaderboard board">
        <option value="act1">Act I — The Unmade Gauntlet</option>
        <option value="act2">Act II — The Undersong</option>
        <option value="act3">Act III — The Anthem</option>
        <option value="campaign">Campaign (legacy)</option>
      </select>
      <select data-category aria-label="Leaderboard category">
        <option value="all">All Runs</option>
        <option value="normal">Normal Clears</option>
        <option value="ascension">Ascension Clears</option>
        <option value="endless">Endless Depth</option>
      </select>
    `;
    this.el.appendChild(filters);

    // Its own label is updated in place rather than by re-rendering the shell:
    // the two selects above render their options without a `selected` marker, so
    // rebuilding them would silently reset what the board LOOKS like filtered to
    // while this.board/this.category kept the old values.
    this.dailyButton = document.createElement('button');
    this.dailyButton.type = 'button';
    this.dailyButton.className = 'menu-button leaderboard-daily-button';
    this.dailyButton.dataset.daily = '';
    this._syncDailyLabel();
    this.dailyButton.addEventListener('click', () => {
      audio.playSfx('click');
      this.dailyOnly = !this.dailyOnly;
      this._syncDailyLabel();
      this._load();
    });
    this.el.appendChild(this.dailyButton);

    filters.querySelector('[data-category]').addEventListener('change', (e) => {
      this.category = e.target.value;
      this._load();
    });
    filters.querySelector('[data-board]').addEventListener('change', (e) => {
      this.board = e.target.value;
      this._load();
    });

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  _syncDailyLabel() {
    this.dailyButton.textContent = this.dailyOnly
      ? `Showing: ${seedLabel(dailySeed())}`
      : 'Showing: every run';
  }

  async _load() {
    this.body.textContent = 'Loading…';
    const result = await LeaderboardClient.getTop({
      limit: 50, category: this.category, board: this.board,
      seed: this.dailyOnly ? dailySeed() : null,
    });
    if (!this.el.isConnected) return; // destroyed while the request was in flight

    if (!result.ok) {
      this.body.textContent = "Couldn't reach the leaderboard. Try again later.";
      return;
    }
    if (result.scores.length === 0) {
      // The daily's empty state is a different sentence: nobody has finished
      // today's seed YET, which is an invitation rather than an empty board.
      this.body.textContent = this.dailyOnly
        ? "No one has finished today's daily yet — be the first."
        : 'No scores yet — be the first to finish a run.';
      return;
    }

    this.body.innerHTML = '';
    const table = document.createElement('div');
    table.className = 'leaderboard-rows scroll-fade';
    const ownName = this.save.accountUsername || this.save.profileName;
    result.scores.forEach((entry, i) => {
      const row = document.createElement('div');
      row.className = 'leaderboard-row';
      if (i < 3) row.classList.add(`leaderboard-row-${i + 1}`); // podium tint
      if (ownName && entry.name === ownName) row.classList.add('leaderboard-row-you');
      // textContent, not innerHTML — entry fields are other players' data.
      const cells = [
        ['leaderboard-rank', `#${i + 1}`],
        ['leaderboard-name', entry.name],
        ['leaderboard-character', entry.character ?? '—'],
        ['leaderboard-score', String(entry.score)],
        ['leaderboard-boss', formatRunDetails(entry)],
        ['leaderboard-time', formatTime(entry.time)],
      ];
      for (const [className, text] of cells) {
        const cell = document.createElement('span');
        cell.className = className;
        cell.textContent = text;
        // The ◇ marker is otherwise unexplained on the board — a tooltip is
        // enough here without spending a column on it.
        if (className === 'leaderboard-boss' && isUnaided(entry)) {
          cell.title = 'Unaided — no meta upgrades, Shard perks, or pet bonuses';
        }
        row.appendChild(cell);
      }
      table.appendChild(row);
    });
    this.body.appendChild(table);
  }

  destroy() {
    this.el.remove();
  }
}
